import { StyleSheet, css } from "aphrodite";
import useOutsideClick from "../hooks/useOutsideClick";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { fadeAnimation } from './dropdownBase.css';
import { CSSTransition } from "react-transition-group";

const DropDownAnimationTime = 150;

export interface DropdownBaseFunctions {
    toggleDropElement: () => void,
}

const DropdownBase = ({
    header,
    dropElement,
    disabled,
    animation,
    preventCloseOnClick,
}:{
    header:         () => ReactNode,
    dropElement:    (functions:DropdownBaseFunctions) => ReactNode,
    disabled?:      boolean,
    animation?:     "fade",
    preventCloseOnClick?: boolean,
}) => {

    const wrapperRef = useRef<HTMLDivElement>(null)
    const [ show, setShow ] = useState(false)

    function toggleDropElement() {
        if (!disabled) {
            setShow(!show)
        }
    }

    useEffect(() => {
        if (disabled && show) {
            setShow(false)
        }
    }, [ show, disabled ])

    const escapeHandler = useCallback((e:KeyboardEvent) => {
        if (e.code === "Escape" && show) {
            setShow(false)
        }
    }, [ show ])

    useOutsideClick(wrapperRef, () => {
        if (show) {
            setShow(false)
        }
    })

    useEffect(() => {
        document.addEventListener("keydown", escapeHandler)
        
        return () => {
            document.removeEventListener("keydown", escapeHandler)
        }
    })

    return (
        <div {...{
            className: css(
                Styles.wrapper, 
                disabled && Styles.disabled, 
                animation === "fade" && fadeAnimation(DropDownAnimationTime)
            ),
            onClick:   () => {
                if (!show || !preventCloseOnClick) {
                    toggleDropElement()
                }
            },
            ref:       wrapperRef,
        }}>
            {header()}
            <CSSTransition {...{
                classNames:    animation === "fade" ? `dropdown-fade` : undefined,
                timeout:       animation === undefined ? 0 : DropDownAnimationTime,
                in:            show,
                mountOnEnter:  true,
                unmountOnExit: true,
            }}>
                {dropElement({
                    toggleDropElement,
                })}
            </CSSTransition>
        </div>
    );
}

export default DropdownBase;

const Styles = StyleSheet.create({
    wrapper: {
        position: "relative",
        cursor:   "pointer",
        margin:  0,
        zIndex:   2,
    },
    disabled: {
        cursor: "default",
        opacity: 0.8,
    },
})